import React from 'react';
import { Box, Card, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import FormTitle from '../../../FormFields/FormTitle';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import { formCardStyle } from '../../../TransitionStyles';
import { CLINICAL } from '../../../formQuestionsEnum';
import { FILE_TYPES } from '../../../formFileTypesEnum';

FileUploadsForm.propTypes = {
  permissions: PropTypes.object,
};

export default function FileUploadsForm({ permissions }) {
  const showForm = [
    CLINICAL.CLINICAL_AND_INFECTION_CONTROL_POLICIES,
    CLINICAL.CLIA_WAIVER,
    CLINICAL.LAB_PERMITS,
    CLINICAL.NURSING_STAFFING_SCHEDULE,
    CLINICAL.LAST_3_ANNUAL_SURVEYS,
    CLINICAL.LAST_3_YEARS_COMPLAINTS,
    CLINICAL.ADMISSION_PAYMENT_BAN_CORRESPONDENCE,
    CLINICAL.QAPI,
    CLINICAL.FACILITY_ASSESSMENT,
  ].some(({ id }) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          File Uploads
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {CLINICAL.CLINICAL_AND_INFECTION_CONTROL_POLICIES.id in permissions && (
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle
                  fieldAttributes={{
                    questionTitle: 'Current clinical and infection control policies',
                    fieldType: 'file',
                  }}
                />
                <FormFileDrop
                  fieldName={CLINICAL.CLINICAL_AND_INFECTION_CONTROL_POLICIES.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.CLINICAL_AND_INFECTION_CONTROL_POLICIES.id],
                    fileTypeId: FILE_TYPES.CLINICAL_INFECTION_CONTROL_POLICIES,
                  }}
                />
              </Box>
            </Grid>
          )}
          {CLINICAL.CLIA_WAIVER.id in permissions && (
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'CLIA waiver', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={CLINICAL.CLIA_WAIVER.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.CLIA_WAIVER.id],
                    fileTypeId: FILE_TYPES.CLIA_WAIVER,
                  }}
                />
              </Box>
            </Grid>
          )}
          {CLINICAL.LAB_PERMITS.id in permissions && (
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'Lab permits', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={CLINICAL.LAB_PERMITS.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.LAB_PERMITS.id],
                    fileTypeId: FILE_TYPES.LAB_PERMITS,
                  }}
                />
              </Box>
            </Grid>
          )}
          {CLINICAL.NURSING_STAFFING_SCHEDULE.id in permissions && (
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'Nursing staffing schedule', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={CLINICAL.NURSING_STAFFING_SCHEDULE.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.NURSING_STAFFING_SCHEDULE.id],
                    fileTypeId: FILE_TYPES.NURSING_STAFFING_SCHEDULE,
                  }}
                />
              </Box>
            </Grid>
          )}
          {CLINICAL.LAST_3_ANNUAL_SURVEYS.id in permissions && (
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'Last 3 annual surveys', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={CLINICAL.LAST_3_ANNUAL_SURVEYS.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.LAST_3_ANNUAL_SURVEYS.id],
                    fileTypeId: FILE_TYPES.ANNUAL_SURVEYS,
                  }}
                />
              </Box>
            </Grid>
          )}
          {CLINICAL.LAST_3_YEARS_COMPLAINTS.id in permissions && (
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'Last 3 years complaints', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={CLINICAL.LAST_3_YEARS_COMPLAINTS.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.LAST_3_YEARS_COMPLAINTS.id],
                    fileTypeId: FILE_TYPES.COMPLAINTS,
                  }}
                />
              </Box>
            </Grid>
          )}
          {CLINICAL.ADMISSION_PAYMENT_BAN_CORRESPONDENCE.id in permissions && (
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle
                  fieldAttributes={{
                    questionTitle: 'Admission/payment ban correspondence last 12 months (DPNA)',
                    fieldType: 'file',
                    information:
                      'All correspondence related to any admission bans, payment bans, threatened decertification, or CMPs within the last 12 months',
                  }}
                />
                <FormFileDrop
                  fieldName={CLINICAL.ADMISSION_PAYMENT_BAN_CORRESPONDENCE.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.ADMISSION_PAYMENT_BAN_CORRESPONDENCE.id],
                    fileTypeId: FILE_TYPES.ADMISSION_AND_PAYMENT_BAN_CORRESPONDENCE,
                  }}
                />
              </Box>
            </Grid>
          )}
          {CLINICAL.QAPI.id in permissions && (
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'Most recent QAPI', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={CLINICAL.QAPI.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.QAPI.id],
                    fileTypeId: FILE_TYPES.QAPI,
                  }}
                />
              </Box>
            </Grid>
          )}
          {CLINICAL.FACILITY_ASSESSMENT.id in permissions && (
            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'Facility assessment', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={CLINICAL.FACILITY_ASSESSMENT.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.FACILITY_ASSESSMENT.id],
                    fileTypeId: FILE_TYPES.FACILITY_ASSESSMENT,
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Card>
    )
  );
}
